import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@shared/lib/utils';
import { Badge, type BadgeProps } from './badge';

const statusBadgeVariants = cva('break-words', {
  variants: {
    tone: {
      positive: '',
      neutral: '',
      warning: '',
      negative: '',
      informational: '',
    },
    appearance: {
      soft: '',
      solid: '',
      outline: '',
    },
  },
  compoundVariants: [
    { tone: 'positive', appearance: 'soft', className: 'status-badge-positive-soft' },
    { tone: 'positive', appearance: 'solid', className: 'status-badge-positive-solid' },
    { tone: 'positive', appearance: 'outline', className: 'status-badge-positive-outline' },
    { tone: 'neutral', appearance: 'soft', className: 'status-badge-neutral-soft' },
    { tone: 'neutral', appearance: 'solid', className: 'status-badge-neutral-solid' },
    { tone: 'neutral', appearance: 'outline', className: 'status-badge-neutral-outline' },
    { tone: 'warning', appearance: 'soft', className: 'status-badge-warning-soft' },
    { tone: 'warning', appearance: 'solid', className: 'status-badge-warning-solid' },
    { tone: 'warning', appearance: 'outline', className: 'status-badge-warning-outline' },
    { tone: 'negative', appearance: 'soft', className: 'status-badge-negative-soft' },
    { tone: 'negative', appearance: 'solid', className: 'status-badge-negative-solid' },
    { tone: 'negative', appearance: 'outline', className: 'status-badge-negative-outline' },
    {
      tone: 'informational',
      appearance: 'soft',
      className: 'status-badge-informational-soft',
    },
    {
      tone: 'informational',
      appearance: 'solid',
      className: 'status-badge-informational-solid',
    },
    {
      tone: 'informational',
      appearance: 'outline',
      className: 'status-badge-informational-outline',
    },
  ],
  defaultVariants: {
    tone: 'neutral',
    appearance: 'soft',
  },
});

export type StatusBadgeTone = NonNullable<VariantProps<typeof statusBadgeVariants>['tone']>;
export type StatusBadgeAppearance = NonNullable<
  VariantProps<typeof statusBadgeVariants>['appearance']
>;

export interface StatusBadgeProps extends Omit<BadgeProps, 'appearance'> {
  tone?: StatusBadgeTone;
  appearance?: StatusBadgeAppearance;
}

export function StatusBadge({
  appearance = 'soft',
  className,
  size = 'md',
  tone = 'neutral',
  wrap = 'wrap',
  ...props
}: StatusBadgeProps) {
  return (
    <Badge
      appearance={null}
      className={cn(statusBadgeVariants({ appearance, tone }), className)}
      data-status-appearance={appearance}
      data-status-tone={tone}
      size={size}
      wrap={wrap}
      {...props}
    />
  );
}

export { statusBadgeVariants };
